import { SCREEN_OBJECT_NUMBERS, SCREENS } from '../../../../../services/domain/wizardScreens'
import { InsertionFlowScreensProps } from '../../../../../@types/props-types'
import ScreenContainer from '../../../../common/containers/screenContainer/ScreenContainer'
import { ScreenLayout } from '../../../../common/containers/screenLayout/ScreenLayout'
import { IconS24List324 } from '@is24/cosma-ui-icons'
import React, { useCallback, useEffect, useMemo } from 'react'
import { TextInput } from '../../../../common/inputs/textInput/TextInput'
import { ObjectNumbersScreenValidationResult } from '../../../../../@types/validation-types'
import { useTranslation } from 'react-i18next'
import { ContentStateProperty, TooltipTypes } from '../../../../../@types/types'
import { ScreenTooltip } from '../../../../common/display/screenTooltip/ScreenTooltip'
import { checkIfOtherExternalIdExist } from '../../../../../api/realEstate/realEstateApi'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import _ from 'lodash'
import { AppDispatch } from '../../../../../@types/store'
import { updateExternalIdValidationMessage } from '../../../../../redux/reducers/insertion/insertionFeature'

const screen = SCREENS[SCREEN_OBJECT_NUMBERS]
const keyPrefix = 'wizard.screen.objectNumbers'

export function ObjectNumbersScreen(props: InsertionFlowScreensProps<ObjectNumbersScreenValidationResult>) {
  const { t } = useTranslation(undefined, { keyPrefix })

  const { validationMessages, insertionData, onNavigate, onChange } = props
  const offerId = useAppSelector((state) => state.insertionFeature.insertionIdentification.id)
  const customExternalIdValidationMessages = useAppSelector(
    (state) => state.insertionFeature.customValidationResults.objectNumbers.fields?.externalId
  )

  const externalIdValidationMessages = useMemo(
    () => [...(validationMessages?.externalId ?? []), ...(customExternalIdValidationMessages ?? [])],
    [validationMessages, customExternalIdValidationMessages]
  )

  const dispatch = useAppDispatch()

  function handleDebounceFn(externalId: string, offerId: string, dispatch: AppDispatch) {
    checkIfOtherExternalIdExist(externalId, offerId)
      .then((r) => {
        dispatch(updateExternalIdValidationMessage({ validationMessages: r ? ['externalIdMustBeUnique'] : [] }))
        return
      })
      .catch((_) => {})
  }

  const debounceFn = useMemo(() => _.debounce(handleDebounceFn, 1000), [])

  const onChangeLocal = useCallback(
    (property: ContentStateProperty, value: string | undefined) => {
      if (property === 'externalId' && value) {
        debounceFn(value, offerId, dispatch)
      }
      onChange({ screen: screen.name, property }, value)
    },
    [debounceFn, dispatch, offerId, onChange]
  )

  useEffect(() => {
    if (insertionData.externalId) {
      handleDebounceFn(insertionData.externalId, offerId, dispatch)
    }
    // run it only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ScreenContainer containerId={screen.id} onNavigate={onNavigate} screen={screen}>
      <ScreenLayout
        id={`${screen.id}`}
        headline={`${keyPrefix}.header`}
        deskTooltip={<Tooltip type={TooltipTypes.DESKTOP} />}
        mobileTooltip={<Tooltip type={TooltipTypes.MOBILE} />}
        headerIcon={<IconS24List324 />}
        className='grid grid-flex gutter-vertical-l'
        screenName={screen.name}
      >
        <div className='grid-item one-whole'>
          <TextInput
            id='external-id'
            label={t('externalId')}
            placeholder={''}
            propertyName={'externalId'}
            value={insertionData.externalId}
            doOnFieldChange={onChangeLocal}
            errors={externalIdValidationMessages}
            onBlur={debounceFn.flush}
          />
        </div>

        <div className='grid-item one-whole'>
          <TextInput
            type='number'
            id='group-number'
            label={t('groupNumber')}
            placeholder={''}
            propertyName='groupNumber'
            value={insertionData.groupNumber}
            doOnFieldChange={onChangeLocal}
            errors={validationMessages?.groupNumber ?? []}
          />
        </div>
      </ScreenLayout>
    </ScreenContainer>
  )
}

function Tooltip(props: { type: TooltipTypes }) {
  return <ScreenTooltip tooltipType={props.type} tooltipKey='tooltip.screens.objectNumbers.objectNumbers' />
}
